import { useEffect, useRef, useState } from "react";
import { api, formatApiError, mediaUrl } from "../api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Upload, Trash2, Copy, Check } from "lucide-react";
import { toast } from "sonner";

export default function MediaAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [copied, setCopied] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/api/media");
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    const next = [...items];
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/api/media/upload", fd);
        next.unshift(data);
      } catch (e) { toast.error(`${file.name}: ${formatApiError(e)}`); }
    }
    setItems(next);
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this file?")) return;
    try {
      await api.delete(`/api/media/${id}`);
      setItems((arr) => arr.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const copy = (item) => {
    const url = mediaUrl(item.url);
    navigator.clipboard.writeText(url);
    setCopied(item.id);
    setTimeout(() => setCopied(null), 1500);
  };

  return (
    <div className="space-y-6" data-testid="media-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-serif font-bold">Media Library</h1>
          <p className="text-sm text-muted-foreground mt-1">All uploaded images across the site.</p>
        </div>
        <div>
          <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => onFiles(e.target.files)} />
          <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="bg-primary hover:bg-primary-dark text-white" data-testid="media-upload-btn">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
            Upload
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : items.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground">Library is empty.</Card>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {items.map((it) => (
            <Card key={it.id} className="overflow-hidden" data-testid={`media-card-${it.id}`}>
              <img src={mediaUrl(it.url)} alt={it.filename} className="w-full aspect-square object-cover" />
              <div className="p-2 space-y-1">
                <div className="text-xs truncate text-muted-foreground">{it.filename}</div>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" className="flex-1" onClick={() => copy(it)}>
                    {copied === it.id ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                  </Button>
                  <Button size="sm" variant="outline" className="text-destructive" onClick={() => remove(it.id)}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
